import {
  FirebaseReducer,
  FirestoreReducer,
  firestoreConnect,
  withFirestore,
} from 'react-redux-firebase';
import { compose } from 'redux';
import { connect } from 'react-redux';
import React, { Component } from 'react';

import './MembersPage.scss';

interface MemberData {
  name: string;
  country: string;
  website: string;
  language: string;
  currency: string;
  address: string;
  keycontact: string;
  telephone: string;
  telephone_alt: string;
  vat: string;
  watchListEmailIds: string;
  allowRating: boolean;
  active: boolean;
  partnerInformationVisible: boolean;
  keyContactEmail: string;
}

interface Member extends MemberData {
  id: string;
  createdAt?: any;
  createdBy?: string;
}

interface MembersPageState {
  showForm: boolean;
  loading: boolean;
  togglingMemberId: string;
  formData: MemberData;
  members: Member[];
  error: string | null;
  successMessage: string | null;
}

class MembersPage extends Component<
  {
    firebase: any;
    firestore: any;
    profile: any;
    partners: any;
  },
  MembersPageState
> {
  constructor(props: any) {
    super(props);
    this.state = {
      showForm: false,
      loading: false,
      togglingMemberId: '',
      formData: {
        name: '',
        country: '',
        website: '',
        language: '',
        currency: 'Euro',
        address: '',
        keycontact: '',
        telephone: '',
        telephone_alt: '',
        vat: '',
        watchListEmailIds: '',
        allowRating: true,
        active: true,
        partnerInformationVisible: true,
        keyContactEmail: '',
      },
      members: [],
      error: null,
      successMessage: null,
    };
  }

  componentDidMount() {
    if (this.props.partners) {
      const partnersArray = Object.entries(this.props.partners)
        .filter(([key]) => key !== 'Admin')
        .map(([id, data]: any) => ({
          id,
          ...(typeof data === 'object' ? data : {}),
        }));
      this.setState({ members: partnersArray });
    }
  }

  componentDidUpdate(prevProps: any) {
    if (this.props.partners !== prevProps.partners && this.props.partners) {
      const partnersArray = Object.entries(this.props.partners)
        .filter(([key]) => key !== 'Admin')
        .map(([id, data]: any) => ({
          id,
          ...(typeof data === 'object' ? data : {}),
        }));
      this.setState({ members: partnersArray });
    }
  }

  handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target as any;
    const formattedValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        [name]: formattedValue,
      },
    }));
  };

  handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    this.setState({ loading: true, error: null, successMessage: null });

    try {
      const { formData } = this.state;

      if (!formData.name.trim()) {
        this.setState({
          error: 'Member name is required',
          loading: false,
        });
        return;
      }

      const createMember = this.props.firebase.functions().httpsCallable('createMember');
      const result = await createMember(formData);

      this.setState({
        successMessage: result.data.message,
        showForm: false,
        formData: {
          name: '',
          country: '',
          website: '',
          language: '',
          currency: 'Euro',
          address: '',
          keycontact: '',
          telephone: '',
          telephone_alt: '',
          vat: '',
          watchListEmailIds: '',
          allowRating: true,
          active: true,
          partnerInformationVisible: true,
          keyContactEmail: '',
        },
        loading: false,
      });

      // Refresh the members list
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error: any) {
      this.setState({
        error: error.message || 'Failed to create member',
        loading: false,
      });
    }
  };

  handleToggleMemberStatus = async (memberId: string, currentActive: boolean) => {
    const nextActive = !currentActive;
    this.setState({ togglingMemberId: memberId, error: null, successMessage: null });

    try {
      const updateMember = this.props.firebase.functions().httpsCallable('updateMember');
      await updateMember({
        memberId,
        updates: {
          active: nextActive,
        },
      });

      this.setState((prevState) => ({
        members: prevState.members.map((member) =>
          member.id === memberId ? { ...member, active: nextActive } : member,
        ),
        togglingMemberId: '',
        successMessage: `Member ${memberId} is now ${nextActive ? 'active' : 'inactive'}`,
      }));
    } catch (error: any) {
      this.setState({
        togglingMemberId: '',
        error: error.message || 'Failed to update member status',
      });
    }
  };

  render() {
    const { showForm, loading, togglingMemberId, formData, members, error, successMessage } =
      this.state;
    const { profile } = this.props;

    // Only admins can access this page
    if (!profile || profile.partnerId !== 'Admin') {
      return (
        <div className="members-page">
          <div className="access-denied">
            <h2>Access Denied</h2>
            <p>Only administrators can manage members.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="members-page">
        <div className="members-container">
          <div className="members-list-section">
            <h2>Members</h2>
            <div className="underline"></div>

            {error && <div className="alert alert-error">{error}</div>}
            {successMessage && <div className="alert alert-success">{successMessage}</div>}

            <div className="members-table-wrapper">
              <table className="members-table">
                <thead>
                  <tr>
                    <th>Member</th>
                    <th>Country</th>
                    <th>Status</th>
                    <th>Active/Inactive</th>
                  </tr>
                </thead>
                <tbody>
                  {members.length > 0 ? (
                    members.map((member) => (
                      <tr key={member.id}>
                        <td>
                          <span className="member-name">{member.name}</span>
                        </td>
                        <td>{member.country || '-'}</td>
                        <td>
                          <span className={`status-badge ${member.active ? 'active' : 'inactive'}`}>
                            {member.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <button
                            className={`status-toggle ${member.active ? 'on' : 'off'}`}
                            onClick={() =>
                              this.handleToggleMemberStatus(member.id, Boolean(member.active))
                            }
                            disabled={togglingMemberId === member.id}
                            title={`Set ${member.name || member.id} as ${
                              member.active ? 'inactive' : 'active'
                            }`}>
                            <span className="status-toggle-label">
                              {member.active ? 'ON' : 'OFF'}
                            </span>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="empty-state">
                        No members found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="create-member-section">
            <h2>Create New Member</h2>
            <div className="underline"></div>

            <form onSubmit={this.handleSubmit} className="create-member-form">
              <div className="form-group">
                <label htmlFor="name">Member</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={this.handleInputChange}
                  placeholder="Enter member name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="country">Country</label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={this.handleInputChange}
                  placeholder="Enter country"
                />
              </div>

              <div className="form-group">
                <label htmlFor="website">Website</label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={this.handleInputChange}
                  placeholder="https://example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="language">Language</label>
                <input
                  type="text"
                  id="language"
                  name="language"
                  value={formData.language}
                  onChange={this.handleInputChange}
                  placeholder="e.g. English/Estonian"
                />
              </div>

              <div className="form-group">
                <label htmlFor="currency">Currency</label>
                <input
                  type="text"
                  id="currency"
                  name="currency"
                  value={formData.currency}
                  onChange={this.handleInputChange}
                  placeholder="Euro"
                />
              </div>

              <div className="form-group">
                <label htmlFor="address">Address</label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={this.handleInputChange}
                  placeholder="Enter member address"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label htmlFor="keycontact">Key Contact</label>
                <input
                  type="text"
                  id="keycontact"
                  name="keycontact"
                  value={formData.keycontact}
                  onChange={this.handleInputChange}
                  placeholder="Contact person name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="keyContactEmail">Key Contact Email</label>
                <input
                  type="email"
                  id="keyContactEmail"
                  name="keyContactEmail"
                  value={formData.keyContactEmail}
                  onChange={this.handleInputChange}
                  placeholder="contact@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="telephone">Telephone</label>
                <input
                  type="tel"
                  id="telephone"
                  name="telephone"
                  value={formData.telephone}
                  onChange={this.handleInputChange}
                  placeholder="+123 456 789"
                />
              </div>

              <div className="form-group">
                <label htmlFor="telephone_alt">Telephone Alt</label>
                <input
                  type="tel"
                  id="telephone_alt"
                  name="telephone_alt"
                  value={formData.telephone_alt}
                  onChange={this.handleInputChange}
                  placeholder="+123 456 789"
                />
              </div>

              <div className="form-group">
                <label htmlFor="vat">VAT</label>
                <input
                  type="text"
                  id="vat"
                  name="vat"
                  value={formData.vat}
                  onChange={this.handleInputChange}
                  placeholder="VAT number"
                />
              </div>

              <div className="form-group">
                <label htmlFor="watchListEmailIds">Watch List Email IDs</label>
                <textarea
                  id="watchListEmailIds"
                  name="watchListEmailIds"
                  value={formData.watchListEmailIds}
                  onChange={this.handleInputChange}
                  placeholder="email1@example.com | email2@example.com"
                  rows={2}
                />
              </div>

              <div className="form-group checkbox">
                <label htmlFor="allowRating">
                  <input
                    type="checkbox"
                    id="allowRating"
                    name="allowRating"
                    checked={formData.allowRating}
                    onChange={this.handleInputChange}
                  />
                  Allow Rating
                </label>
              </div>

              <div className="form-group checkbox">
                <label htmlFor="active">
                  <input
                    type="checkbox"
                    id="active"
                    name="active"
                    checked={formData.active}
                    onChange={this.handleInputChange}
                  />
                  Active
                </label>
              </div>

              <div className="form-group checkbox">
                <label htmlFor="partnerInformationVisible">
                  <input
                    type="checkbox"
                    id="partnerInformationVisible"
                    name="partnerInformationVisible"
                    checked={formData.partnerInformationVisible}
                    onChange={this.handleInputChange}
                  />
                  Partner Information Visible
                </label>
              </div>

              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Adding...' : 'Add'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  profile: state.firebase.profile,
  partners: state.firestore.data.partners,
});

export default compose(
  withFirestore,
  connect(mapStateToProps),
  firestoreConnect([
    {
      collection: 'partners',
    },
  ]),
)(MembersPage);
